import MDTypography from "components/MDTypography";
import { IconButton, Select, MenuItem, Tooltip, styled, Stack } from "@mui/material";
import MDButton from "components/MDButton";
import colors from "assets/theme-dark/base/colors";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { MatcherView, PhaseData } from "utils/MatcherProvider";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState, useRef, useCallback } from "react";

const Row = styled(Stack)`
  flex-direction: row;
  gap: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

interface Duplicate {
  FX_UNIQUE_ID: string;
  NAME: string;
  COUNT: number;
}

interface TableRow {
  FX_UNIQUE_ID: string;
  [key: string]: unknown;
}

interface TableData {
  columns: { Header: string; accessor: string }[];
  rows: TableRow[];
}

interface SelectedCube {
  cubeId: string;
}

interface MergeData {
  rowId?: string;
  value?: string;
}

interface DuplicateSelectorProps {
  duplicates: Duplicate[];
  selectedDuplicate: Duplicate | null;
  onSelectDuplicate: (duplicate: Duplicate) => void;
  mergeData: MergeData | null;
  handleMergeRecords: (args: {
    cubeId: string;
    fxId: string;
    identifier: string;
    tableName: string;
    value: string;
  }) => void;
  loading: { service: boolean; merge: boolean };
  phaseData: PhaseData;
  selectedCube: SelectedCube;
  tableData: TableData;
  hasSearched: boolean;
  searchTableData: TableRow[];
  changeView: (view: MatcherView) => void;
}

export function DuplicateSelector({
  duplicates,
  selectedDuplicate,
  onSelectDuplicate,
  mergeData,
  handleMergeRecords,
  loading,
  phaseData,
  selectedCube,
  tableData,
  hasSearched,
  searchTableData,
  changeView,
}: DuplicateSelectorProps) {
  const [visibleDuplicates, setVisibleDuplicates] = useState<Duplicate[]>([]);
  const [page, setPage] = useState<number>(1);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const pageSize = 20;
  const totalPages = Math.ceil(duplicates.length / pageSize);

  useEffect(() => {
    setVisibleDuplicates(duplicates.slice(0, pageSize));
    setPage(1);
  }, [duplicates]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const target = event.target as HTMLDivElement;
        const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
        if (bottom && page < totalPages) {
          const nextPage = page + 1;
          setVisibleDuplicates((prev) => [...prev, ...duplicates.slice(page * pageSize, nextPage * pageSize)]);
          setPage(nextPage);
        }
      }, 100);
    },
    [page, totalPages, duplicates, pageSize]
  );

  const handleMerge = () => {
    if (!selectedDuplicate || !mergeData) {
      enqueueSnackbar("Please select a duplicate and a matcher row", { variant: "error" });
      return;
    }
    const source = hasSearched ? searchTableData : tableData.rows;
    const rightRow = source.find((row: TableRow) => row[phaseData.identifier] === mergeData.value);
    if (!rightRow) {
      enqueueSnackbar("Selected matcher row not found", { variant: "error" });
      return;
    }
    const identifierValue = rightRow[phaseData.identifier] as string;
    if (!identifierValue) {
      enqueueSnackbar(`Identifier ${phaseData.identifier} not found in row`, { variant: "error" });
      return;
    }
    handleMergeRecords({
      cubeId: selectedCube.cubeId,
      fxId: selectedDuplicate.FX_UNIQUE_ID,
      identifier: phaseData.identifier.toString(),
      tableName: phaseData.tableName,
      value: identifierValue.toString(),
    });
  };

  return (
    <div style={{ maxHeight: 200 }}>
      <Row justifyContent="space-between" sx={{ alignItems: "center" }}>
        <MDTypography mb="1rem" ml="1rem" variant="h3">
          Duplicates
        </MDTypography>
        <Tooltip title="Go to Results Page" arrow>
          <IconButton
            onClick={() => changeView(MatcherView.RESULTS)}
            sx={{ color: colors.secondary.main }}
            aria-label="Go to results page"
          >
            <ArrowOutwardIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      </Row>
      <Row justifyContent="space-between" sx={{ alignItems: "center" }}>
        <Row sx={{ gap: "0.5rem", alignItems: "center" }}>
          <Select
            sx={{
              ml: "1rem",
              width: "25rem",
              height: "36px",
              borderRadius: "8px",
              boxSizing: "border-box",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: colors.grey[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.grey[500],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                padding: "8px",
                fontSize: "0.875rem",
                color: colors.text.main,
                boxSizing: "border-box",
                height: "36px",
                display: "flex",
                alignItems: "center",
              },
            }}
            size="small"
            renderValue={(selected: string) => {
              const selectedItem = duplicates.find((duplicate: Duplicate) => duplicate.FX_UNIQUE_ID === selected);
              return selectedItem ? selectedItem.NAME : <em style={{ fontSize: "0.875rem" }}>Select an item</em>;
            }}
            inputProps={{ "aria-label": "Select duplicate record" }}
            displayEmpty
            value={selectedDuplicate ? selectedDuplicate.FX_UNIQUE_ID : ""}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: "300px",
                  width: "25rem",
                },
                onScroll: handleScroll,
              },
            }}
          >
            {visibleDuplicates?.map((duplicate: Duplicate, index: number) => (
              <MenuItem
                onClick={() => onSelectDuplicate(duplicate)}
                value={duplicate.FX_UNIQUE_ID}
                key={duplicate.FX_UNIQUE_ID || index}
                sx={{ fontSize: "0.875rem" }}
              >
                {duplicate.NAME}
              </MenuItem>
            ))}
          </Select>
          <MDButton
            variant="background"
            loading={loading.merge}
            disabled={!mergeData || !selectedDuplicate || !mergeData.value}
            color="secondary"
            onClick={handleMerge}
            sx={{
              height: "36px",
              padding: "0 12px",
              fontSize: "0.875rem",
              minWidth: "80px",
              borderRadius: "8px",
              lineHeight: "36px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& .MuiButton-label": {
                display: "flex",
                alignItems: "center",
                height: "100%",
              },
            }}
          >
            Merge
          </MDButton>
        </Row>
      </Row>
    </div>
  );
}
